'use client';

import { FC, memo } from 'react';

import { useData } from '@/components';
import type { HeaderSlice } from '@/types';

import { HeaderContainer } from './Header.components';
import {
  HeaderDefaultDesktop,
  HeaderDefaultMobile,
  HeaderWithCenteredLogoAndBottomContentDesktop,
  HeaderWithCenteredLogoAndLocatorDesktop,
  HeaderWithCenteredLogoAndLocatorFixDesktop,
  HeaderWithCenteredLogoAndLocatorFixMobile,
  HeaderWithCenteredLogoAndLocatorMobile,
  HeaderWithCenteredLogoAndNavDesktop,
  HeaderWithCenteredLogoAndNavMobile,
  HeaderWithHorizontalNavMobile,
  HeaderWithOneLineContentNavLeftDesktop,
  HeaderWithOneLineContentNavRightDesktop,
  HeaderWithRightStoreLocatorDesktop,
  HeaderWithTopLocatorAndBottomNavDesktop,
  HeaderWithTopLocatorAndBottomNavMobile,
  HeaderWithTwoLinesWithLeftLogoMobile,
} from './variations';

type HeaderVariation = HeaderSlice['variation'];

const MobileComponentsByVariation: Record<HeaderVariation, FC> = {
  default: HeaderDefaultMobile,
  withRightStoreLocator: HeaderDefaultMobile,
  withTopLocatorAndBottomNav: HeaderWithTopLocatorAndBottomNavMobile,
  withCenteredLogoAndNav: HeaderWithCenteredLogoAndNavMobile,
  withCenteredLogoAndLocator: HeaderWithCenteredLogoAndLocatorMobile,
  withCenteredLogoAndLocatorFix: HeaderWithCenteredLogoAndLocatorFixMobile,
  withHorizontalNav: HeaderWithHorizontalNavMobile,
  withCenteredLogoAndBottomContent: HeaderWithHorizontalNavMobile,
  withOneLineContentNavRight: HeaderWithTwoLinesWithLeftLogoMobile,
  withOneLineContentNavLeft: HeaderWithTwoLinesWithLeftLogoMobile,
  withTwoLinesWithLeftLogo: HeaderWithTwoLinesWithLeftLogoMobile,
};

const DesktopComponentsByVariation: Record<HeaderVariation, FC> = {
  default: HeaderDefaultDesktop,
  withRightStoreLocator: HeaderWithRightStoreLocatorDesktop,
  withTopLocatorAndBottomNav: HeaderWithTopLocatorAndBottomNavDesktop,
  withCenteredLogoAndNav: HeaderWithCenteredLogoAndNavDesktop,
  withCenteredLogoAndLocator: HeaderWithCenteredLogoAndLocatorDesktop,
  withCenteredLogoAndLocatorFix: HeaderWithCenteredLogoAndLocatorFixDesktop,
  withHorizontalNav: HeaderDefaultDesktop,
  withCenteredLogoAndBottomContent:
    HeaderWithCenteredLogoAndBottomContentDesktop,
  withOneLineContentNavRight: HeaderWithOneLineContentNavRightDesktop,
  withOneLineContentNavLeft: HeaderWithOneLineContentNavLeftDesktop,
  withTwoLinesWithLeftLogo: HeaderDefaultDesktop,
};

const renderHeaderMobileVariation = (variation: HeaderVariation) => {
  const Layout =
    MobileComponentsByVariation[variation] ??
    MobileComponentsByVariation.default;
  return <Layout />;
};

const renderHeaderDesktopVariation = (variation: HeaderVariation) => {
  const Layout =
    DesktopComponentsByVariation[variation] ??
    DesktopComponentsByVariation.default;
  return <Layout />;
};

const Header = memo(() => {
  const { header } = useData();
  const { activeDesktop, activeMobile } = header.data;

  return (
    <HeaderContainer>
      <HeaderContainer.Mobile>
        {renderHeaderMobileVariation(activeMobile.variation)}
      </HeaderContainer.Mobile>
      <HeaderContainer.Desktop>
        {renderHeaderDesktopVariation(activeDesktop.variation)}
      </HeaderContainer.Desktop>
    </HeaderContainer>
  );
});

export { Header, renderHeaderDesktopVariation, renderHeaderMobileVariation };
export type { HeaderVariation };
